/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-imports */
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from '@material-ui/core';
import { GetApp as DownloadIcon, Refresh as RefreshIcon } from '@material-ui/icons';

import { Skeleton } from '@mui/material';
import { PageHeader } from 'components/Contigo';
import { contigoService } from 'services';

import DetailedTab from './components/DetailedTab';
import InvoicesTab from './components/InvoicesTab';
import ShipmentsTab from './components/ShipmentsTab';
import SummaryTab from './components/SummaryTab';
import { useStyles } from './styles';
import { exportReportToExcel } from './utils/excelExport';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'CONFIRMED', label: 'Confirmado' },
  { value: 'PENDING_SAMSUNG', label: 'Aguardando Samsung' },
  { value: 'AWAITING_DELIVERY', label: 'Aguardando Entrega' },
  { value: 'IN_FULFILLMENT', label: 'Em Atendimento' },
  { value: 'PARTIALLY_FULFILLED', label: 'Parcialmente Atendido' },
  { value: 'FULFILLED', label: 'Atendido' },
  { value: 'PARTIALLY_INVOICED', label: 'Parcialmente Faturado' },
  { value: 'INVOICED', label: 'Faturado' },
  { value: 'COMPLETED', label: 'Concluído' },
  { value: 'CANCELED', label: 'Cancelado' },
];

export default function Reports() {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [reportData, setReportData] = useState(null);

  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    return {
      start_date: sevenDaysAgo.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0],
    };
  };

  const [filters, setFilters] = useState({
    ...getDefaultDates(),
    status: '',
  });

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      const params = {};

      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.status) params.status = filters.status;

      const [ordersResponse, shipmentsResponse] = await Promise.all([
        contigoService.getOrdersReport(params),
        contigoService.listShipments(params),
      ]);

      setReportData({
        ...ordersResponse.data,
        shipments: shipmentsResponse.data || [],
      });
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleExport = async () => {
    if (!reportData) return;

    try {
      setExporting(true);
      await exportReportToExcel(reportData);
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar relatório');
    } finally {
      setExporting(false);
    }
  };

  const handleBack = () => {
    history.push('/contigo/orders');
  };

  if (loading && !reportData) {
    return (
      <Box className={classes.container}>
        <Skeleton variant='text' width='40%' height={40} style={{ marginBottom: 24 }} />
        <Skeleton variant='rectangular' height={200} style={{ marginBottom: 16 }} />
        <Skeleton variant='rectangular' height={400} />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <PageHeader
        onBack={handleBack}
        title='Relatórios de Pedidos'
        subtitle='Visualize e exporte dados consolidados dos pedidos'
      />

      {/* Filtros */}
      <Card className={classes.filtersCard}>
        <CardContent>
          <Box className={classes.filtersContent}>
            <TextField
              label='Data Início'
              type='date'
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              size='small'
              className={classes.filterField}
            />
            <TextField
              label='Data Fim'
              type='date'
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              size='small'
              className={classes.filterField}
            />
            <TextField
              select
              label='Status'
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              variant='outlined'
              size='small'
              className={classes.filterField}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant='contained'
              color='primary'
              startIcon={<RefreshIcon />}
              onClick={loadReport}
              disabled={loading}
            >
              Aplicar Filtros
            </Button>
            {reportData && (
              <Button
                variant='outlined'
                color='primary'
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={exporting}
                className={classes.exportButton}
              >
                Exportar Excel
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {reportData && (
        <Card>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            indicatorColor='primary'
            textColor='primary'
          >
            <Tab label='Resumo' />
            <Tab label={`Pedidos (${reportData.detailed?.length || 0})`} />
            <Tab label={`Notas Fiscais (${reportData.invoices?.length || 0})`} />
            <Tab label={`Expedições (${reportData.shipments?.length || 0})`} />
          </Tabs>

          <Box className={classes.tabContent}>
            {activeTab === 0 && <SummaryTab data={reportData} classes={classes} />}
            {activeTab === 1 && <DetailedTab data={reportData} classes={classes} />}
            {activeTab === 2 && <InvoicesTab data={reportData} classes={classes} />}
            {activeTab === 3 && <ShipmentsTab data={reportData} classes={classes} />}
          </Box>
        </Card>
      )}

      <Backdrop open={loading || exporting} style={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </Box>
  );
}
